import {
  Box,
  Card,
  Divider,
  FormControl,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  RadioProps,
} from "@mui/joy";
import * as React from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PaymentService } from "../../services/payment.service";
import { Toggler } from "../utils";
import { KeyboardArrowDownOutlined } from "@mui/icons-material";
import { TagIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { KycPaymentGatewayService } from "../../services/kyc-payment-gateway.service";
import { KycPaymentGatewayStatus } from "../../types/kyc";
import { ApplicableAdminFeeTypes } from "../../types/applicable-admin-fee.enum";
import { HeroIcon } from "../stateless/icons";
import { BrowserRouterConstants } from "../../utils/constants";
import { Link } from "react-router-dom";
import { formatCurrencyByCode } from "../../utils/formatPrice";

interface Props {
  control: any;
  setValue: any;
  onlinePaymentOnly?: boolean;
  paymentAmount?: number | null;
  paymentCurrency?: string;
  paymentCountry?: string;
  paymentMethods?: any;
  transactionType?: "credit" | "order";
  sellerId?: string | null;
  sellerCurrency?: string | "IDR";
  applicableAdminFee?: ApplicableAdminFeeTypes;
  handleChangePaymentFee?: (arg0: number) => void;
  loanPaymentActive?: boolean;
  isChargedPlatformFee?: boolean;
}

const MethodIcons = [
  {
    name: "va",
    icon: <HeroIcon name="CubeTransparentIcon" />,
  },
  {
    name: "e-wallet",
    icon: <HeroIcon name="WalletIcon" />,
  },
  {
    name: "retail",
    icon: <HeroIcon name="TagIcon" />,
  },
  {
    name: "qris",
    icon: <HeroIcon name="QrCodeIcon" />,
  },
  {
    name: "credit-card",
    icon: <HeroIcon name="CreditCardIcon" />,
  },
];

const MethodDescriptions = [
  {
    name: "va",
    description:
      "You can use virtual account with selected bank to proceed the transaction",
  },
  {
    name: "e-wallet",
    description:
      "You can choose payment with e-wallet to proceed the transaction",
  },
  {
    name: "retail",
    description:
      "You can go to the near retail store to proceed the transaction",
  },
  {
    name: "qris",
    description: "You can scan this QR code to proceed the transaction",
  },
  {
    name: "credit-card",
    description:
      "You can use a credit or debit card to proceed the transaction",
  },
];

const MethodIcon = ({ method }: { method: string }) => {
  return (
    MethodIcons.find((m) => m.name === method)?.icon ?? (
      <TagIcon style={{ width: "1.5rem", height: "1.5rem" }} />
    )
  );
};

const PaymentOptions = ({
  control,
  setValue,
  onlinePaymentOnly = false,
  paymentAmount = 0,
  paymentCurrency = "IDR",
  paymentMethods = null,
  transactionType = "order",
  applicableAdminFee = ApplicableAdminFeeTypes.CHARGE_TO_SELLER,
  handleChangePaymentFee = () => {},
  sellerId,
  sellerCurrency = "IDR",
  loanPaymentActive = false,
  isChargedPlatformFee = true,
}: Props) => {
  console.log("Amount", paymentAmount);
  const { t } = useTranslation();
  const { data, refetch } = useQuery({
    queryKey: ["kyc-payment-gateway-status-payment"],
    queryFn: () => KycPaymentGatewayService.getVerificationStatus(sellerId),
  });
  const [_adminfee, setAdminFee] = React.useState<number>(0);
  const [availableOnlinePayments, setAvailableOnlinePayments] = React.useState<
    any[]
  >([]);

  const RadioCard = (props: RadioProps) => {
    return (
      <Card
        sx={{
          width: "100%",
          backgroundColor: "var(--joy-palette-neutral-50)",
          borderColor: "transparent",
          borderRadius: "24px",
          padding: "24px",
        }}
        variant="outlined"
      >
        <Radio
          color="primary"
          sx={{
            flexDirection: "row-reverse",
            paddingBottom: "20px",
            borderBottom: "1px solid var(--joy-palette-neutral-200)",
          }}
          {...props}
        />
      </Card>
    );
  };

  React.useEffect(() => {
    console.log("PAYEMTN CURRENCY", {
      paymentCurrency,
      sellerCurrency,
    });
    if (
      import.meta.env.VITE_ONLINE_PAYMENT_ENABLED === "true" &&
      !paymentMethods &&
      paymentAmount
      // Allow multi currency PG transaction on one business
      // && sellerCurrency === paymentCurrency
    ) {
      PaymentService.getAvailbleOnlinePaymentMethods(
        paymentCurrency,
        sellerId ?? "",
        paymentAmount
      ).then((res) => {
        console.log("RESPONSE", res);
        setAvailableOnlinePayments(res.data);
      });
    }
    refetch();
  }, [paymentAmount]);

  // Update state when `paymentMethods` or env var changes
  React.useEffect(() => {
    if (
      paymentMethods &&
      import.meta.env.VITE_ONLINE_PAYMENT_ENABLED === "true"
    ) {
      setAvailableOnlinePayments(paymentMethods); // ✅ Update existing state
    }
  }, [paymentMethods]);

  // TODO: maybe put this in a useContainer so its reusable
  const handleCalculatePaymentFee = (method: string) => {
    if (method.includes("offline-payment")) {
      setAdminFee(0);
      handleChangePaymentFee?.(0);
      return;
    }
    console.log("METHOD", method);
    const methodAndOptions = method.split(":");
    const selectedMethod = availableOnlinePayments.find(
      (item: any) => item.method === methodAndOptions[0]
    );
    const platformFee = isChargedPlatformFee
      ? selectedMethod.platformFee ?? 0
      : 0;
    if (applicableAdminFee === ApplicableAdminFeeTypes.CHARGE_TO_SELLER) {
      setAdminFee(0 + platformFee);
      handleChangePaymentFee?.(0 + platformFee);
      return;
    }
    console.log("FEE", selectedMethod, method);
    if (selectedMethod) {
      // Use method fee first
      var fee =
        selectedMethod.feeType === "amount"
          ? selectedMethod.fee
          : (selectedMethod.fee / 100) * (paymentAmount ?? 0);
      if (methodAndOptions.length == 2) {
        const optionsFee = selectedMethod.options.find(
          (op: any) => op.code === methodAndOptions[1]
        );
        // If there is an option fee, ignore the method fee
        if (optionsFee && optionsFee.fee) {
          fee =
            optionsFee.feeType === "amount"
              ? optionsFee.fee
              : (optionsFee.fee / 100) * (paymentAmount ?? 0);
        }
      }
      if (sellerCurrency === "IDR") {
        fee = Math.ceil(fee);
      }
      setAdminFee(fee + platformFee);
      handleChangePaymentFee?.(fee + platformFee);
      return;
    }
    return;
  };

  console.log("Available Online Payments ::", availableOnlinePayments);

  return (
    <Stack maxWidth={"100%"} spacing={2}>
      <Stack>
        <Controller
          name={"paymentMethod"}
          defaultValue={""}
          control={control}
          render={({ field: { value } }) => (
            <FormControl>
              <RadioGroup
                defaultValue="medium"
                name="payment-radio-buttons-group"
                onChange={(_event) => {
                  setValue("paymentMethod", _event.target.value);
                  handleCalculatePaymentFee(_event.target.value);
                }}
                value={value}
              >
                <Stack spacing={2}>
                  {(data?.status === KycPaymentGatewayStatus.VALIDATED ||
                    transactionType === "credit") &&
                    // true &&
                    availableOnlinePayments &&
                    availableOnlinePayments
                      .filter((p: any) => p.isActive)
                      .map((item: any) => (
                        <Card
                          key={item.method}
                          sx={{
                            width: "100%",
                            backgroundColor: "#F9FAFB",
                            borderColor: "transparent",
                            borderRadius: "24px",
                            padding: "24px",
                          }}
                          variant="outlined"
                        >
                          {/* Card Option - Credit Cards */}
                          {item?.options && item?.options?.length > 0 ? (
                            <>
                              <Toggler
                                renderToggle={({
                                  open,
                                  setOpen,
                                  sxClasses,
                                }) => (
                                  <Stack
                                    direction={"row"}
                                    justifyContent={"space-between"}
                                    onClick={() => setOpen(!open)}
                                    sx={sxClasses}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "start",
                                        justifyContent: "center",
                                        gap: 1,
                                      }}
                                    >
                                      {item.icon ? (
                                        <img
                                          src={item.icon}
                                          style={{
                                            width: "2rem",
                                            height: "2rem",
                                            backgroundColor: "#F9FAFB",
                                            objectFit: "contain",
                                          }}
                                        />
                                      ) : (
                                        <MethodIcon method={item.method} />
                                      )}
                                      <Stack>
                                        <Typography
                                          level="text-md-bold"
                                          sx={sxClasses}
                                          onClick={() => setOpen(!open)}
                                        >
                                          {item.name}
                                        </Typography>
                                        <Typography level="text-sm-regular">
                                          {MethodDescriptions.find(
                                            (desc) => desc.name === item.method
                                          )?.description ?? ""}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                    <KeyboardArrowDownOutlined
                                      sx={{
                                        transform: open
                                          ? "rotate(180deg)"
                                          : "none",
                                      }}
                                    />
                                  </Stack>
                                )}
                              >
                                <Stack marginLeft={"2rem"} spacing={2}>
                                  {item.options
                                    .filter((o: any) => o.isActive)
                                    .map((option: any) => (
                                      <Radio
                                        key={option.code}
                                        value={`${item.method}:${option.code}`}
                                        label={
                                          <Typography
                                            level="text-md-regular"
                                            startDecorator={
                                              <img
                                                src={
                                                  option.icon?.includes("http")
                                                    ? option.icon
                                                    : `${
                                                        import.meta.env
                                                          .VITE_SPACES_HOST
                                                      }${option.icon}`
                                                }
                                                style={{
                                                  width: "2rem",
                                                  height: "2rem",
                                                  backgroundColor: "#F9FAFB",
                                                  objectFit: "contain",
                                                }}
                                              />
                                            }
                                          >
                                            {option.name}
                                          </Typography>
                                        }
                                        color="primary"
                                        sx={{
                                          flexDirection: "row-reverse",
                                          paddingBottom: "20px",
                                          borderBottom:
                                            "1px solid var(--joy-palette-neutral-200)",
                                        }}
                                        slotProps={{
                                          input: {
                                            "aria-describedby":
                                              "ep-helper-text",
                                          },
                                          radio: {
                                            style: {
                                              alignSelf: "center",
                                            },
                                          },
                                        }}
                                      />
                                    ))}
                                </Stack>
                              </Toggler>
                              <Divider />
                            </>
                          ) : (
                            <>
                              {item.service == "linkz-bnpl" && !item.limit ? (
                                <Stack
                                  sx={{
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    paddingBottom: "20px",
                                    borderBottom:
                                      "1px solid var(--joy-palette-neutral-200)",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "start",
                                      gap: 1,
                                      width: {
                                        xs: "48%",
                                        lg: "75%",
                                      },
                                    }}
                                  >
                                    {item.icon ? (
                                      <img
                                        src={item.icon}
                                        style={{
                                          width: "2rem",
                                          height: "2rem",
                                          backgroundColor: "#F9FAFB",
                                          objectFit: "contain",
                                        }}
                                      />
                                    ) : (
                                      <MethodIcon method={item.method} />
                                    )}
                                    <Stack>
                                      <Typography level="text-md-bold">
                                        {item.name}
                                      </Typography>
                                      <Typography
                                        level="text-sm-regular"
                                        color="neutral"
                                      >
                                        {t("finance.payment.description")}
                                      </Typography>
                                    </Stack>
                                  </Box>
                                  <Link
                                    to={`${
                                      BrowserRouterConstants.ContactUs
                                    }?text=${t("dashboard.contactUsText")}`}
                                    target="_blank"
                                    className="bg-[#499873] p-2  rounded-lg text-white text-sm font-semibold items-center"
                                  >
                                    {t("dashboard.contactUs")}
                                  </Link>
                                </Stack>
                              ) : (
                                <Radio
                                  value={item.method}
                                  disabled={
                                    (item.service == "linkz-bnpl" &&
                                      !item.limit.isActive) ||
                                    (item.service == "linkz-bnpl" &&
                                      _adminfee + (paymentAmount ?? 0) >
                                        item.limit.remainingLimit)
                                  }
                                  color="primary"
                                  slotProps={{
                                    input: {
                                      "aria-describedby": "ep-helper-text",
                                    },
                                    radio: {
                                      style: {
                                        alignSelf: "center",
                                      },
                                    },
                                  }}
                                  sx={{
                                    flexDirection: "row-reverse",
                                    paddingBottom: "20px",
                                    borderBottom:
                                      "1px solid var(--joy-palette-neutral-200)",
                                  }}
                                  label={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "start",
                                        gap: 1,
                                      }}
                                    >
                                      {item.icon ? (
                                        <img
                                          src={item.icon}
                                          style={{
                                            width: "2rem",
                                            height: "2rem",
                                            backgroundColor: "#F9FAFB",
                                            objectFit: "contain",
                                          }}
                                        />
                                      ) : (
                                        <MethodIcon method={item.method} />
                                      )}
                                      <Stack>
                                        <Typography level="text-md-bold">
                                          {item.name}
                                        </Typography>
                                        <Typography
                                          level="text-sm-regular"
                                          color="neutral"
                                        >
                                          {item.service == "linkz-bnpl" &&
                                          item.limit.isActive ? (
                                            <>
                                              <p>
                                                {t(
                                                  "order.paymentModal.bizPayLaterDescription"
                                                )}{" "}
                                                {(paymentAmount ?? 0) +
                                                  _adminfee >
                                                  item.limit.remainingLimit}
                                                <span
                                                  className={`font-semibold ${
                                                    (paymentAmount ?? 0) +
                                                      _adminfee >
                                                      item.limit
                                                        .remainingLimit &&
                                                    "text-danger-500"
                                                  }`}
                                                >
                                                  {formatCurrencyByCode(
                                                    item.limit.remainingLimit,
                                                    item.limit.currency
                                                  )}
                                                </span>
                                              </p>
                                              <p>
                                                {" "}
                                                {t(
                                                  "order.paymentModal.bizPayLaterTerm"
                                                )}{" "}
                                                <span className="font-semibold">
                                                  {item.limit.repaymentPeriod}
                                                  {" days"}
                                                </span>
                                              </p>
                                              {(paymentAmount ?? 0) +
                                                _adminfee >
                                                item.limit.remainingLimit && (
                                                <p className="text-danger-500">
                                                  {" "}
                                                  {t(
                                                    "order.paymentModal.notEnoughCredit"
                                                  )}
                                                </p>
                                              )}
                                            </>
                                          ) : (
                                            MethodDescriptions.find(
                                              (desc) =>
                                                desc.name === item.method
                                            )?.description ?? ""
                                          )}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                  }
                                />
                              )}
                            </>
                          )}
                        </Card>
                      ))}

                  {/* No Online Payment - Loan and Offline */}
                  {!onlinePaymentOnly && (
                    <>
                      {loanPaymentActive && (
                        <RadioCard
                          value="loan-payment"
                          label={
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "start",
                                  gap: 1,
                                }}
                              >
                                <HeroIcon name="CreditCardIcon" />
                                <Stack>
                                  <Typography level="text-md-bold">
                                    {" "}
                                    {t("order.paymentModal.loanLabel")}
                                  </Typography>

                                  <Typography
                                    level="text-sm-regular"
                                    color="neutral"
                                  >
                                    {t("order.paymentModal.loanDesc")}
                                  </Typography>
                                </Stack>
                              </Box>
                            </>
                          }
                          slotProps={{
                            input: {
                              "aria-describedby": "rfl-helper-text",
                            },
                            radio: {
                              style: {
                                alignSelf: "center",
                              },
                            },
                          }}
                        />
                      )}

                      <RadioCard
                        value="offline-payment"
                        label={
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "start",
                                gap: 1,
                              }}
                            >
                              <HeroIcon name="BanknotesIcon" />
                              <Stack>
                                <Typography level="text-md-bold">
                                  {" "}
                                  {t("order.paymentModal.offlineLabel")}
                                </Typography>
                                <br />
                                <Typography
                                  color="neutral"
                                  fontSize={"sm"}
                                  fontWeight={"sm"}
                                >
                                  {t("order.paymentModal.offlineDesc")}
                                </Typography>
                              </Stack>
                            </Box>
                          </>
                        }
                        slotProps={{
                          input: {
                            "aria-describedby": "op-helper-text",
                          },
                          radio: {
                            style: {
                              alignSelf: "center",
                            },
                          },
                        }}
                      />
                    </>
                  )}
                </Stack>
              </RadioGroup>
            </FormControl>
          )}
        />
      </Stack>
    </Stack>
  );
};

export default PaymentOptions;
